// cSpell:ignore aandeel ASBL BVBA CVBA dagen Eigenaar eigenaar eigenaarsaandeel excls forfait Huurdersdeel kost leeg leegstand Leegstand LUMINUS Maarten OPPERVLAKTE overschot Pcts privé Scenariolaan SPRL VERDELING Verdeling VOLGENS Volledig
/* eslint-disable no-nested-ternary */
// What a party's row of the summary says about how its amount came about
// (Lewis, 2026-10-18). The party cards show the figures in their table (one
// row per invoice); what this adds is who carries the rest of the unit's
// share, in the "i" of a card's costs, and the days an owner's unit stood
// empty:
//
//   Rest van bus 6 (61 dagen): Nina € 29,04 · leegstand € 30,00 (eigenaar)
//
// Every amount comes from the engine's own figures (each tenant's
// costByInvoice and vatByInvoice, each owner's lines): nothing is split
// again here. A figure the result does not hold exactly (an older
// snapshot, an owner part spread over several units) leaves the line out,
// never guessed.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  compareUnitLabels,
  isUnitBreakdownKey,
} from "../../../../buildingSettlement.utils";
import {
  ComputeResult,
  daysInPeriod,
  mergedCoverage,
  occupantKey,
  OwnerCostLine,
  OwnerResult,
} from "../../../../engine";
import { settledLeaseDaysWithin } from "../../../../fixedFeePeriods.utils";
import {
  existedSince,
  IAddressParts,
  keySplitsForCosts,
  unitAddressLabel,
  unitBoxLabel,
} from "../../../../groupUnits.utils";
import {
  fromCents,
  roundCents,
  splitCents,
  toCents,
} from "../../../../money.utils";
import { formatWeight } from "../../../../numberInput.utils";
import { IOccupiedDays } from "../../../../occupiedDays.utils";
import { ownerLineVatByCause } from "../../../../ownerLineCauses.utils";
import { settlementDisplayName } from "../../../../settlementName.utils";
import {
  Category,
  CONSUMPTION_UNIT,
  ContractRef,
  DistributionMethod,
  IAlreadySettledLine,
  Invoice,
  isDepartureSettlement,
  PerContractResult,
  resolveDistributionMethod,
  Settlement,
  settlementAmount,
} from "../../../../types";
import { btwOf, fmt } from "./shared";
import type { CatMeta, ICardDistribution } from "./summaryCards.utils";

const PRIVATE_SUFFIX = " (privé)";
// A cent either way: two amounts that differ less are the same amount.
const HALF_CENT = 0.005;
// How far a unit's share read from the parties may lie from the key's
// ratio of the cost (each party's VAT is rounded on its own).
const KEY_RATIO_TOLERANCE = 0.03;

// The row of a party card that was clicked: whose share, of which breakdown
// key ("Electricity", "Water (privé)"), and what the row shows.
export type TCostShareParty =
  | { party: "tenant"; key: string; contractId: string }
  | {
      party: "owner";
      key: string;
      kind: "base" | "vacancy" | "departure_difference";
      // The owner's units (see OwnerResult.unitIds); absent for the one
      // owner card of an older snapshot.
      unitIds?: string[];
    };

export type TCostShareRow = TCostShareParty & {
  // The row's amount incl. VAT.
  amount: number;
  // Only this invoice of the row's category: one row of a party card.
  onlyInvoiceId?: string;
};

// A lease and the days it ran, for the days a unit stood empty.
export interface ICostShareLease {
  contractId: string;
  unitId: string;
  from: string;
  to: string;
}

// How the categories were split: the draft's choices or the snapshot's.
export interface ICostShareSplitFigures {
  distributionMethod?: Record<string, DistributionMethod>;
  consumption?: Record<string, Record<string, number>>;
  buildingTotal?: Record<string, number>;
  excessMode?: Record<string, "key" | "landlord">;
}

// What the summary knows about the whole settlement.
export interface ICostShareContext {
  perContract: PerContractResult[];
  // Absent on older snapshots.
  owners?: OwnerResult[];
  ownerNames?: Record<string, string>;
  catMeta: Record<string, CatMeta>;
  distribution?: ICardDistribution;
  leases: ICostShareLease[];
  split?: ICostShareSplitFigures;
  isDeparture?: boolean;
  // A departed lease's share of a cost its departure already settled: only
  // the days after it are charged here, so its days do not explain it.
  alreadySettled?: IAlreadySettledLine[];
  // A unit by its full address, "Scenariolaan 1 bus 6, 9000 Gent": never
  // "bus 6" alone, which names nothing for a unit without a box (Lewis,
  // 2026-10-19).
  unitLabelOf: (unitId: string) => string;
  // The confirmed settlement the costs are in.
  lockedIn?: string;
  // The viewer sees only their own part (an owner of some of the units):
  // a split's total is theirs, not the cost's (Y16).
  viewerPart?: boolean;
  // Per cost split by readings or a metering company's amounts, each unit's
  // part excl. VAT (ComputeResult.unitSharesByInvoice): what an owner's
  // vacancy of each unit is when the excess goes by the key (M19).
  unitSharesByInvoice?: Record<string, Record<string, number>>;
}

export interface ICostShareCalculation {
  // "Rest van bus 6 (61 dagen): Nina € 29,04 · leegstand € 30,00 (eigenaar)".
  rest?: string;
}

// ── Names and labels ────────────────────────────────────────────────────────

const COMPANY_FORM =
  /\b(BV|NV|BVBA|CV|CVBA|VZW|VOF|CommV|SRL|SA|SPRL|ASBL)\.?$/i;

// "Maarten", "Jonas & Karen": the first name of each tenant on the lease, as
// a step names them; a company keeps its whole name.
export const shortPartyName = (name: string): string =>
  name
    .split(" & ")
    .map(part => {
      const trimmed = part.trim();
      if (COMPANY_FORM.test(trimmed)) return trimmed;
      return trimmed.split(/\s+/)[0] || trimmed;
    })
    .join(" & ");

// A name written in capitals ("LUMINUS", "VERDELING VOLGENS OPPERVLAKTE")
// in sentence case, as the card writes everything; a short acronym ("BV")
// stays as it is.
const MIN_CAPITALS = 4;
export const sentenceCaseOf = (text: string): string => {
  const letters = text.match(/[A-Za-zÀ-ÿ]/g) ?? [];
  if (letters.length < MIN_CAPITALS || text !== text.toLocaleUpperCase("nl")) {
    return text;
  }
  const lower = text.toLocaleLowerCase("nl");
  return lower.charAt(0).toLocaleUpperCase("nl") + lower.slice(1);
};

const categoryOfKey = (key: string): Category =>
  (key.endsWith(PRIVATE_SUFFIX)
    ? key.slice(0, -PRIVATE_SUFFIX.length)
    : key) as Category;

const costDays = (inv: Invoice): number =>
  daysInPeriod(inv.periodFrom, inv.periodTo);

const costPeriodOf = (inv: Invoice): { from: string; to: string } => ({
  from: inv.periodFrom,
  to: inv.periodTo,
});

// The days a lease's share of a cost counts: the days it ran in the cost's
// period, without the days the cost's kind was a forfait for it (N71), as
// the engine and the statement count them.
const settledDaysOf = (
  lease: ICostShareLease,
  pc: PerContractResult,
  inv: Invoice,
): number =>
  settledLeaseDaysWithin({
    lease,
    range: costPeriodOf(inv),
    fixedFeeDays:
      pc.forfaitIntervalsBySlice?.[
        inv.scope === "common" ? "common" : "private"
      ],
  });

const inclOf = (inv: Invoice): number =>
  roundCents(inv.amountExclVat + btwOf(inv));

const sum = (amounts: number[]): number =>
  roundCents(amounts.reduce((total, amount) => total + amount, 0));

// The sum of amounts that are all known, or undefined.
const sumKnown = (amounts: (number | undefined)[]): number | undefined =>
  amounts.every((amount): amount is number => amount != null)
    ? sum(amounts as number[])
    : undefined;

const sameAmount = (a: number, b: number): boolean =>
  Math.abs(toCents(a) - toCents(b)) === 0;

const sameUnitSet = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every(id => b.map(String).includes(String(id)));

// ── The figures of one cost ─────────────────────────────────────────────────

const createReader = (context: ICostShareContext, key: string) => {
  const category = categoryOfKey(key);
  const isPrivate = isUnitBreakdownKey(key);
  const dist = context.distribution?.byCat[key];
  const method: DistributionMethod = isPrivate
    ? "key"
    : dist?.key
      ? "key"
      : context.split?.distributionMethod
        ? resolveDistributionMethod(
            category,
            context.split.distributionMethod,
            { isDeparture: context.isDeparture },
          )
        : "key";
  const isSplitByDays = isPrivate || method === "key";

  // A tenant's share of a cost incl. VAT, as the engine charged it: 0
  // when they have none, undefined when the result does not say.
  const tenantShareOf = (
    pc: PerContractResult,
    inv: Invoice,
  ): number | undefined => {
    if (!pc.costByInvoice) return undefined;
    const excl = pc.costByInvoice[inv.id];
    if (excl == null) return 0;
    const vat = pc.vatByInvoice?.[inv.id];
    return vat == null ? undefined : roundCents(excl + vat);
  };

  const leasesOf = (unitId: string): ContractRef[] =>
    context.leases
      .filter(lease => String(lease.unitId) === String(unitId))
      .map(lease => ({
        id: lease.contractId,
        unitId: lease.unitId,
        unitCode: "",
        tenantName: "",
        startDate: lease.from,
        endDate: lease.to,
      }));

  const coveredDaysOf = (unitId: string, inv: Invoice): number =>
    mergedCoverage(leasesOf(unitId), {
      from: inv.periodFrom.slice(0, 10),
      to: inv.periodTo.slice(0, 10),
    }).coveredDays;

  // The key's split of one cost: a unit's weight and the basis.
  const keySplitOf = (inv: Invoice) => {
    if (!dist?.key || !context.distribution) return undefined;
    return keySplitsForCosts({
      keyDef: dist.key,
      units: context.distribution.units,
      periods: [{ from: inv.periodFrom, to: inv.periodTo }],
      allUnits: context.distribution.allUnits,
      unitCount: context.distribution.unitCount,
    })[0];
  };

  const unitExisted = (unitId: string, inv: Invoice): boolean => {
    const unit = [
      ...(context.distribution?.allUnits ?? []),
      ...(context.distribution?.units ?? []),
    ].find(candidate => String(candidate.id) === String(unitId));
    return existedSince({ archivedOn: unit?.archivedOn, from: inv.periodFrom });
  };

  const hasFixedFee = (unitId: string): boolean =>
    context.perContract.some(
      pc =>
        String(pc.unitId) === String(unitId) &&
        (pc.fixedFeeKinds ?? []).length > 0,
    );

  // Whether the owner of a unit carries nothing of a group cost: the
  // tenants take all of it (no fixed owner share), the key gives the unit
  // no weight, or its leases cover every day of the cost.
  const ownerCarriesNothingOf = (unitId: string, inv: Invoice): boolean => {
    if (inv.scope === "private") return String(inv.unitId) !== String(unitId);
    if (!unitExisted(unitId, inv)) return true;
    if (method !== "key") return false;
    if (!sameAmount(settlementAmount(inv), inv.amountExclVat)) return false;
    if (hasFixedFee(unitId)) return false;
    const split = keySplitOf(inv);
    if (split && split.total > 0 && split.weightOf(String(unitId)) <= 0) {
      return true;
    }
    return coveredDaysOf(unitId, inv) >= costDays(inv);
  };

  const ownerOfUnit = (unitId: string): OwnerResult | undefined =>
    context.owners?.find(owner =>
      owner.unitIds.map(String).includes(String(unitId)),
    );

  const ownerLineOf = (
    owner: OwnerResult,
    inv: Invoice,
  ): OwnerCostLine | undefined =>
    owner.lines.find(line => line.invoiceId === inv.id && line.label === key);

  // What the owner of a unit carries of a cost for that unit, incl. VAT:
  // known when their line for the cost can only come from this unit.
  const ownerPartOfUnit = (
    unitId: string,
    inv: Invoice,
  ): number | undefined => {
    if (ownerCarriesNothingOf(unitId, inv)) return 0;
    const owner = ownerOfUnit(unitId);
    // The result lists every owner with a part: one it does not list
    // carries nothing. An older snapshot without owners does not say.
    if (!owner) return context.owners ? 0 : undefined;
    const line = ownerLineOf(owner, inv);
    if (!line) return 0;
    if ((line.departureDiffs ?? []).length > 0) return undefined;
    const isOnlyThisUnit =
      inv.scope === "private" ||
      owner.unitIds
        .filter(id => String(id) !== String(unitId))
        .every(id => ownerCarriesNothingOf(id, inv));
    return isOnlyThisUnit ? roundCents(line.amount + line.vat) : undefined;
  };

  const tenantsOfUnit = (unitId: string) =>
    context.perContract.filter(pc => String(pc.unitId) === String(unitId));

  // The unit's share of a cost incl. VAT: its tenants' and its owner's
  // parts together, as the engine charged them. A departure settlement
  // holds only the leaving tenant, so the unit's other parties are unknown.
  const unitShareOf = (unitId: string, inv: Invoice): number | undefined => {
    if (context.isDeparture) return undefined;
    // A reading or a metering company's amount: only a unit whose leases
    // cover the whole cost keeps all of its part with its tenants (the
    // excess and the unassigned rest go to the owners over every unit).
    if (!isSplitByDays) {
      if (context.split?.excessMode?.[category] === "key") return undefined;
      if (coveredDaysOf(unitId, inv) < costDays(inv)) return undefined;
      return sumKnown(tenantsOfUnit(unitId).map(pc => tenantShareOf(pc, inv)));
    }
    const share = sumKnown([
      ...tenantsOfUnit(unitId).map(pc => tenantShareOf(pc, inv)),
      ownerPartOfUnit(unitId, inv),
    ]);
    if (share == null) return undefined;
    // The key's ratio of the cost says what the unit's share must be, up
    // to the parties' VAT cents: anything further off is no share of this
    // unit alone.
    const split = inv.scope === "common" ? keySplitOf(inv) : undefined;
    if (split && split.total > 0) {
      const expected =
        (inclOf(inv) * split.weightOf(String(unitId))) / split.total;
      if (Math.abs(expected - share) > KEY_RATIO_TOLERANCE) return undefined;
    }
    return share;
  };

  // A unit's own cost the owner shares in ("Huurdersdeel 60%"): what its
  // tenants carry together, known when they lived there every day of it
  // (the owner's vacancy and fixed share are one figure otherwise).
  const tenantPortionOf = (
    unitId: string,
    inv: Invoice,
  ): number | undefined => {
    if (inv.scope !== "private" || inv.target !== "shared") {
      return unitShareOf(unitId, inv);
    }
    if (context.isDeparture) return undefined;
    if (coveredDaysOf(unitId, inv) < costDays(inv)) return undefined;
    return sumKnown(tenantsOfUnit(unitId).map(pc => tenantShareOf(pc, inv)));
  };

  return {
    category,
    isPrivate,
    dist,
    method,
    isSplitByDays,
    tenantShareOf,
    coveredDaysOf,
    keySplitOf,
    ownerCarriesNothingOf,
    ownerPartOfUnit,
    ownerLineOf,
    ownerOfUnit,
    tenantsOfUnit,
    unitShareOf,
    tenantPortionOf,
  };
};

// ── Who carries the rest ────────────────────────────────────────────────────

const restText = (params: {
  unitLabel: string;
  days?: number;
  parties: string[];
}): string | undefined => {
  const { unitLabel, days, parties } = params;
  if (parties.length === 0) return undefined;
  return days != null
    ? getLocalizedText("settlement.share_card.rest", {
        unit: unitLabel,
        days: String(days),
        parties: parties.join(" · "),
      })
    : getLocalizedText("settlement.share_card.rest_no_days", {
        unit: unitLabel,
        parties: parties.join(" · "),
      });
};

// The owner's part of a unit in the rest line: its vacancy, or its fixed
// share as well.
const ownerRestParty = (amount: number, isVacancyOnly: boolean): string =>
  getLocalizedText(
    isVacancyOnly
      ? "settlement.share_card.rest_vacancy"
      : "settlement.share_card.rest_owner",
    { amount: fmt(amount) },
  );

// The costs of a row's category, or its one invoice.
const costsOfRow = (
  row: TCostShareRow,
  context: ICostShareContext,
): Invoice[] =>
  (context.catMeta[row.key]?.costs ?? []).filter(
    inv => !row.onlyInvoiceId || inv.id === row.onlyInvoiceId,
  );

// A tenant's row: who carries the rest of their unit's share, when the
// row is the tenant's whole share and every other part of the unit is
// known.
const tenantCalculation = (
  row: Extract<TCostShareRow, { party: "tenant" }>,
  context: ICostShareContext,
): ICostShareCalculation => {
  const reader = createReader(context, row.key);
  const pc = context.perContract.find(
    candidate => candidate.contractId === row.contractId,
  );
  if (!pc) return {};
  const allCosts = costsOfRow(row, context);
  const withShare = pc.costByInvoice
    ? allCosts.filter(inv => pc.costByInvoice?.[inv.id] != null)
    : [];
  const costs = withShare.length > 0 ? withShare : allCosts;
  const unitId = pc.unitId ?? "";
  const unitLabel = context.unitLabelOf(pc.unitId);

  // The tenant's share of each cost; a category of one cost on an older
  // snapshot is the row itself.
  const perCost = costs.map(inv => reader.tenantShareOf(pc, inv));
  const tenantShares: (number | undefined)[] =
    perCost.some(share => share == null) && allCosts.length === 1
      ? [row.amount]
      : perCost;
  const tenantTotal = sumKnown(tenantShares);
  if (tenantTotal == null || !sameAmount(tenantTotal, row.amount)) return {};
  // A reading or an amount per tenant is the tenant's alone: no rest.
  if (!reader.isSplitByDays && reader.dist?.perOccupant) return {};

  // The unit's share. A unit's own cost the owner shares in: the tenants'
  // part of it ("Huurdersdeel 60%"); a mix of parts is no one share.
  const sharedPcts = new Set(
    costs.map(inv => (inv.target === "shared" ? inv.tenantSharePct ?? 0 : 100)),
  );
  const isTenantsPart =
    reader.isPrivate && costs.some(inv => inv.target === "shared");
  const unitTotal =
    isTenantsPart && sharedPcts.size > 1
      ? undefined
      : sumKnown(
          costs.map(inv =>
            isTenantsPart
              ? reader.tenantPortionOf(unitId, inv)
              : reader.unitShareOf(unitId, inv),
          ),
        );
  if (unitTotal == null) return {};
  if (roundCents(unitTotal - tenantTotal) <= HALF_CENT) return {};

  // The unit's other tenants and its owner.
  const others = reader
    .tenantsOfUnit(unitId)
    .filter(other => other.contractId !== pc.contractId)
    .map(other => ({
      name: shortPartyName(other.tenantName),
      amount: sumKnown(costs.map(inv => reader.tenantShareOf(other, inv))),
    }));
  // The tenants' part of a shared own cost leaves the owner's out: the
  // unit had no empty day (see tenantPortionOf).
  const ownerPart = isTenantsPart
    ? 0
    : sumKnown(costs.map(inv => reader.ownerPartOfUnit(unitId, inv)));
  if (others.some(other => other.amount == null) || ownerPart == null) {
    return {};
  }
  const unitOwner = reader.ownerOfUnit(unitId);
  const isVacancyOnly = costs.every(inv => {
    const line = unitOwner && reader.ownerLineOf(unitOwner, inv);
    return !line || Math.abs(line.base) < HALF_CENT;
  });
  const parties = [
    ...others
      .filter(other => (other.amount as number) > HALF_CENT)
      .map(other => `${other.name} ${fmt(other.amount as number)}`),
    ...(ownerPart > HALF_CENT
      ? [ownerRestParty(ownerPart, isVacancyOnly)]
      : []),
  ];

  // The rest's days, for one period: the cost's days the tenant did not
  // live there.
  const lease = context.leases.find(
    candidate => candidate.contractId === pc.contractId,
  );
  const periods = new Set(
    costs.map(inv => `${inv.periodFrom}|${inv.periodTo}`),
  );
  const tenantDays =
    lease && costs[0] ? settledDaysOf(lease, pc, costs[0]) : undefined;
  return {
    rest: restText({
      unitLabel,
      days:
        periods.size === 1 && tenantDays != null && costs[0]
          ? costDays(costs[0]) - tenantDays
          : undefined,
      parties,
    }),
  };
};

// An owner's row: who carries the rest of the unit's share, when one unit
// of theirs explains the row (the days it stood empty: ownerVacancyOf).
const ownerCalculation = (
  row: Extract<TCostShareRow, { party: "owner" }>,
  context: ICostShareContext,
): ICostShareCalculation => {
  const reader = createReader(context, row.key);
  const owners = context.owners ?? [];
  const owner = row.unitIds
    ? owners.find(candidate =>
        sameUnitSet(candidate.unitIds, row.unitIds ?? []),
      )
    : owners.length === 1
      ? owners[0]
      : undefined;
  const allCosts = costsOfRow(row, context);
  const withLine = owner
    ? allCosts.filter(inv => reader.ownerLineOf(owner, inv))
    : [];
  const costs = withLine.length > 0 ? withLine : allCosts;

  // The one unit of the owner the row's part comes from, when there is one.
  const ownUnitIds = owner?.unitIds.map(String) ?? row.unitIds ?? [];
  const contributing = ownUnitIds.filter(unitId =>
    costs.some(inv => !reader.ownerCarriesNothingOf(unitId, inv)),
  );
  const unitId =
    row.kind !== "departure_difference" && contributing.length === 1
      ? contributing[0]
      : undefined;
  if (unitId == null) return {};
  const unitLabel = context.unitLabelOf(unitId);

  // The days the unit stood empty, for one period split by days.
  const isSinglePeriod =
    new Set(costs.map(inv => `${inv.periodFrom}|${inv.periodTo}`)).size === 1;
  const vacantDays =
    isSinglePeriod && costs[0] && reader.isSplitByDays
      ? costDays(costs[0]) - reader.coveredDaysOf(unitId, costs[0])
      : undefined;

  // The rest of the unit: its tenants, when every part of the unit is known
  // and the owner's part of it is this row.
  const unitTotal = sumKnown(costs.map(inv => reader.unitShareOf(unitId, inv)));
  const ownerPart = sumKnown(
    costs.map(inv => reader.ownerPartOfUnit(unitId, inv)),
  );
  if (
    !unitLabel ||
    unitTotal == null ||
    ownerPart == null ||
    !sameAmount(ownerPart, row.amount)
  ) {
    return {};
  }
  const tenants = reader.tenantsOfUnit(unitId).map(pc => ({
    name: shortPartyName(pc.tenantName),
    amount: sumKnown(costs.map(inv => reader.tenantShareOf(pc, inv))),
  }));
  if (
    tenants.some(tenant => tenant.amount == null) ||
    roundCents(unitTotal - ownerPart) <= HALF_CENT
  ) {
    return {};
  }
  return {
    rest: restText({
      unitLabel,
      days:
        vacantDays != null && costs[0]
          ? costDays(costs[0]) - vacantDays
          : undefined,
      parties: tenants
        .filter(tenant => (tenant.amount as number) > HALF_CENT)
        .map(tenant => `${tenant.name} ${fmt(tenant.amount as number)}`),
    }),
  };
};

// The owner's units that stood empty during a cost, which gave them its
// vacancy part, each with the days it stood empty, and those days when it
// is the only one (Lewis, 2026-10-19): the Leegstand row of the owner's
// card. None for a cost not split by the days.
export const ownerVacancyOf = (params: {
  key: string;
  invoice: Invoice;
  unitIds: string[];
  context: ICostShareContext;
}):
  | {
      unitIds: string[];
      days?: { days: number; total: number };
      daysByUnit: { unitId: string; days: number; total: number }[];
    }
  | undefined => {
  const { key, invoice: inv, unitIds, context } = params;
  const reader = createReader(context, key);
  if (!reader.isSplitByDays) return undefined;
  const total = costDays(inv);
  const split = inv.scope === "common" ? reader.keySplitOf(inv) : undefined;
  const vacantIds = unitIds.map(String).filter(unitId => {
    if (inv.scope === "private" && String(inv.unitId) !== unitId) return false;
    if (split && split.total > 0 && split.weightOf(unitId) <= 0) return false;
    return reader.coveredDaysOf(unitId, inv) < total;
  });
  if (vacantIds.length === 0) return undefined;
  const daysByUnit = vacantIds.map(unitId => ({
    unitId,
    days: total - reader.coveredDaysOf(unitId, inv),
    total,
  }));
  return {
    unitIds: vacantIds,
    daysByUnit,
    ...(daysByUnit.length === 1
      ? { days: { days: daysByUnit[0].days, total } }
      : {}),
  };
};

export const costShareCalculation = (params: {
  row: TCostShareRow;
  context: ICostShareContext;
}): ICostShareCalculation => {
  const { row, context } = params;
  return row.party === "tenant"
    ? tenantCalculation(row, context)
    : ownerCalculation(row, context);
};

// ── A reading's vacancy per unit ────────────────────────────────────────────
// A cost split by a reading per occupant (M19 and M29, retest of
// 2026-10-20): an owner's vacancy is, per empty unit, its own reading's
// part and, when the excess goes by the key, the unit's share of the excess.
// The engine keeps one vacancy figure per owner and cost; per unit it is
// what the unit's frozen part (unitSharesByInvoice) leaves after its
// tenants, its departures and their differences. Without those figures,
// or with a fixed owner share in the same line, each unit's own reading
// stands for it, as before.

export interface IReadingVacancyPart {
  unitId: string;
  // The unit's own reading of its vacant days.
  reading: number;
  // Its part of the owner's vacancy, excl. VAT; undefined when the
  // readings alone weigh it (no excess in it).
  excl?: number;
  // What its reading costs, excl. VAT, when the excess is in `excl`.
  readingPart?: number;
}

const readingVacancyParts = (params: {
  inv: Invoice;
  context: ICostShareContext;
  reader: ReturnType<typeof createReader>;
  unitIds: string[];
  line: Pick<OwnerCostLine, "base" | "vacancy" | "departureDiffs">;
}): IReadingVacancyPart[] | undefined => {
  const { inv, context, reader, unitIds, line } = params;
  if (reader.method !== "consumption" || !reader.dist?.perOccupant) {
    return undefined;
  }
  const readings = context.split?.consumption?.[reader.category] ?? {};
  const total = context.split?.buildingTotal?.[reader.category] ?? 0;
  const readingOf = (unitId: string) =>
    readings[occupantKey.vacant(unitId)] ?? 0;
  const byReadings = unitIds.map(unitId => ({
    unitId,
    reading: readingOf(unitId),
  }));
  const shares = context.unitSharesByInvoice?.[inv.id];
  if (
    context.split?.excessMode?.[reader.category] !== "key" ||
    !shares ||
    total <= 0 ||
    Math.abs(line.base) >= HALF_CENT
  ) {
    return byReadings;
  }
  const tenantCentsOf = (unitId: string) =>
    reader
      .tenantsOfUnit(unitId)
      .reduce((sum, pc) => sum + toCents(pc.costByInvoice?.[inv.id] ?? 0), 0);
  const heldCentsOf = (unitId: string) =>
    (context.alreadySettled ?? [])
      .filter(
        held =>
          held.invoiceId === inv.id && String(held.unitId) === String(unitId),
      )
      .reduce((sum, held) => sum + toCents(held.amount), 0);
  const diffCentsOf = (unitId: string) =>
    (line.departureDiffs ?? [])
      .filter(diff => String(diff.unitId) === String(unitId))
      .reduce((sum, diff) => sum + toCents(diff.amount), 0);
  const ownerCents = unitIds.map(unitId =>
    Math.max(
      0,
      toCents(shares[unitId] ?? 0) -
        tenantCentsOf(unitId) -
        heldCentsOf(unitId) -
        diffCentsOf(unitId),
    ),
  );
  // Within a cent per unit of the owner's vacancy, or the figures are not
  // this line's (an older snapshot): the readings then weigh it.
  const sumCents = ownerCents.reduce((sum, cents) => sum + cents, 0);
  if (Math.abs(sumCents - toCents(line.vacancy)) > unitIds.length) {
    return byReadings;
  }
  const allCents = Object.values(shares).reduce(
    (sum, amount) => sum + toCents(amount),
    0,
  );
  return byReadings.map((part, index) => ({
    ...part,
    excl: fromCents(ownerCents[index]),
    readingPart: fromCents(Math.round((allCents * part.reading) / total)),
  }));
};

// "5 m³ + aandeel overschot € 31,50", "aandeel overschot € 51,10" or
// "5/100 m³": what a reading's vacancy of `excl` stands for.
const readingVacancyText = (params: {
  reading: number;
  total?: number;
  unit?: string;
  excess: number;
}): string => {
  const { reading, total, unit, excess } = params;
  const withUnit = (text: string) => `${text}${unit ? ` ${unit}` : ""}`;
  if (Math.abs(excess) >= HALF_CENT) {
    return reading > 0
      ? getLocalizedText("settlement.summary.card.reading_plus_excess", {
          reading: withUnit(formatWeight(reading)),
          amount: fmt(excess),
        })
      : getLocalizedText("settlement.summary.card.excess_share", {
          amount: fmt(excess),
        });
  }
  return total
    ? withUnit(`${formatWeight(reading)}/${formatWeight(total)}`)
    : withUnit(formatWeight(reading));
};

// What an owner's vacancy of a cost split by a reading per occupant stands
// for, over all of their units ("5 m³ + aandeel overschot € 82,60"): the
// owner card's share of its Leegstand row (M29). Undefined for another
// method.
export const ownerReadingVacancyText = (params: {
  key: string;
  invoice: Invoice;
  unitIds: string[];
  line: Pick<OwnerCostLine, "base" | "vacancy" | "departureDiffs">;
  context: ICostShareContext;
}): string | undefined => {
  const { key, invoice: inv, unitIds, line, context } = params;
  const reader = createReader(context, key);
  const parts = readingVacancyParts({ inv, context, reader, unitIds, line });
  if (!parts) return undefined;
  const reading = parts.reduce((sum, part) => sum + part.reading, 0);
  const readingPart = parts.every(part => part.readingPart != null)
    ? parts.reduce((sum, part) => sum + toCents(part.readingPart ?? 0), 0)
    : undefined;
  return readingVacancyText({
    reading,
    total: context.split?.buildingTotal?.[reader.category],
    unit: CONSUMPTION_UNIT[reader.category],
    excess:
      readingPart == null ? 0 : fromCents(toCents(line.vacancy) - readingPart),
  });
};

// ── One cost's split over its parties ───────────────────────────────────────
// The "Verdeling" of the cost popup (Lewis, 2026-10-18): one row per tenant
// per unit, the days a unit stood empty and the owner's fixed share, with a
// total equal to the cost. In a settlement's context every amount is the
// engine's own figure for this invoice, as the summary shows it.

// Whose row of a summary opened the cost: a tenant's lease, or an owner's
// units.
export interface ICostSplitParty {
  contractId?: string;
  ownerUnitIds?: string[];
}

export interface ICostSplitRow {
  id: string;
  kind: "tenant" | "vacancy" | "owner";
  // "bus 2", or the units of an owner of several ("bus 1, bus 7"); absent
  // for a property on its own.
  unitLabel?: string;
  // "Maarten Scenario", "leegstand (eigenaar Els)".
  name: string;
  // The "Wie" of the cost's split (Lewis, 2026-10-19): the tenant's name,
  // "Leegstand" or "Eigenaar (aandeel kost 50%)" on the first line, and
  // small under it the owner ("eigenaar Els") and the unit's full address.
  title: string;
  notes: string[];
  // "10/85", "Volledig", "40%", "120/900 kWh".
  share?: string;
  // The days the tenant lived there ("30/30"), or the unit stood empty
  // ("30/30 leeg"), of the cost's period.
  occupied?: IOccupiedDays;
  // Incl. VAT.
  amount: number;
  // The VAT in `amount`, as the split charged it: the cost popup shows the
  // amount excl. VAT with this under it.
  vat: number;
  // The unit the row is about, when it is one.
  unitId?: string;
  contractId?: string;
  // An owner's row: all of the owner's units, to match the summary's card.
  ownerUnitIds?: string[];
}

export interface ICostSplit {
  rows: ICostSplitRow[];
  // What the split still needs, said instead of figures that would be a
  // guess.
  missing?: string;
}

const ROW_KIND_ORDER: Record<ICostSplitRow["kind"], number> = {
  tenant: 0,
  vacancy: 1,
  owner: 2,
};

// By unit as a person counts them ("bus 2" before "bus 10"), the tenants
// first, then the vacancy and the owner's share; an owner of several units
// after the units.
export const sortCostSplitRows = (rows: ICostSplitRow[]): ICostSplitRow[] =>
  rows
    .map((row, index) => ({ row, index }))
    .sort(
      (a, b) =>
        compareUnitLabels(a.row.unitLabel ?? "", b.row.unitLabel ?? "") ||
        ROW_KIND_ORDER[a.row.kind] - ROW_KIND_ORDER[b.row.kind] ||
        a.index - b.index,
    )
    .map(({ row }) => row);

// "leegstand (eigenaar Els)", "eigenaarsaandeel (eigenaar Els)".
export const ownerPartName = (
  part: "vacancy" | "owner",
  ownerName?: string,
): string =>
  ownerName
    ? getLocalizedText(
        part === "vacancy"
          ? "costs.detail.split.vacancy_of_owner"
          : "costs.detail.split.owner_share_of_owner",
        { owner: ownerName },
      )
    : getLocalizedText(
        part === "vacancy"
          ? "costs.detail.split.vacancy"
          : "costs.detail.split.owner_share",
      );

// The tenants' part of a unit's own cost the owner shares in; none (a dash)
// when the unit carries all of it (Lewis, 2026-10-19: not "Volledig").
export const unitCostShareText = (
  tenantSharePct: number,
): string | undefined =>
  tenantSharePct < 100
    ? getLocalizedText("settlement.summary.card.split_pct", {
        pct: String(tenantSharePct),
      })
    : undefined;

const breakdownKeyOf = (inv: Invoice): string =>
  inv.scope === "private" ? `${inv.category}${PRIVATE_SUFFIX}` : inv.category;

// The split of one cost in a settlement's figures (a confirmed one's
// snapshot, or the wizard's draft): the engine's amount of each party for
// this invoice, never split again here.
export const costSplitFromContext = (params: {
  invoice: Invoice;
  context: ICostShareContext;
}): ICostSplit => {
  const { invoice: inv, context } = params;
  const key = breakdownKeyOf(inv);
  const reader = createReader(context, key);
  const totalDays = costDays(inv);
  const tenantPct = inv.target === "shared" ? inv.tenantSharePct ?? 0 : 100;
  const perOccupant = !reader.isSplitByDays && !!reader.dist?.perOccupant;
  // A building's cost, or a cost of a settlement over several units: each
  // row names its unit.
  const namesUnits =
    !reader.isPrivate || (context.distribution?.units.length ?? 0) > 0;
  const unitLabelOf = (unitId: string): string | undefined =>
    namesUnits ? context.unitLabelOf(unitId) : undefined;
  // The full address of each of the row's units, one per line.
  const addressNotesOf = (unitIds: string[]): string[] =>
    unitIds.map(unitId => context.unitLabelOf(unitId)).sort(compareUnitLabels);

  // The unit's share of the cost: its key's ratio, its reading of the
  // building's total, the method of a metering company's amount, or all of
  // a unit's own cost.
  const unitShareOf = (
    unitId: string,
    contractId?: string,
  ): string | undefined => {
    if (reader.isPrivate) return unitCostShareText(tenantPct);
    if (reader.method === "key") {
      const split = reader.keySplitOf(inv);
      return split && split.total > 0
        ? `${formatWeight(split.weightOf(String(unitId)))}/${formatWeight(
            split.total,
          )}`
        : undefined;
    }
    if (reader.method === "consumption") {
      const readings = context.split?.consumption?.[reader.category] ?? {};
      const reading =
        perOccupant && contractId
          ? readings[`contract:${contractId}`]
          : readings[unitId];
      const total = context.split?.buildingTotal?.[reader.category];
      if (reading != null && total != null && total > 0) {
        const unit = CONSUMPTION_UNIT[reader.category];
        // A share of the excess by the key on top of the reading (Y14).
        const excess = contractId
          ? context.perContract.find(pc => pc.contractId === contractId)
              ?.excessByInvoice?.[inv.id] ?? 0
          : 0;
        if (Math.abs(excess) >= 0.005) {
          return getLocalizedText(
            "settlement.summary.card.reading_plus_excess",
            {
              reading: `${formatWeight(reading)}${unit ? ` ${unit}` : ""}`,
              amount: fmt(excess),
            },
          );
        }
        return `${formatWeight(reading)}/${formatWeight(total)}${
          unit ? ` ${unit}` : ""
        }`;
      }
    }
    return reader.dist?.label;
  };

  const rows: ICostSplitRow[] = [];
  let isUnknown = false;
  // A departed tenant's share that their departure settlement settled
  // already: part of the cost, outside this settlement's own figures, and
  // before the unit's later tenants.
  (context.alreadySettled ?? [])
    .filter(line => line.invoiceId === inv.id)
    .forEach(line => {
      const name = getLocalizedText("costs.detail.split.settled_at_departure", {
        tenant: line.tenantName,
      });
      rows.push({
        id: `departed-${line.settlementId}-${line.contractId}`,
        kind: "tenant",
        unitId: line.unitId,
        unitLabel: unitLabelOf(line.unitId),
        name,
        title: name,
        notes: addressNotesOf([line.unitId]),
        share: unitShareOf(line.unitId, line.contractId),
        amount: roundCents(line.amount + line.vat),
        vat: line.vat,
        contractId: line.contractId,
      });
    });
  // A unit's tenants in the order they lived there.
  const leaseFromOf = (pc: PerContractResult): string =>
    context.leases.find(lease => lease.contractId === pc.contractId)?.from ??
    "";
  const tenants = [...context.perContract].sort((a, b) =>
    leaseFromOf(a).localeCompare(leaseFromOf(b)),
  );
  for (const pc of tenants) {
    const amount = reader.tenantShareOf(pc, inv);
    if (amount == null) {
      isUnknown = true;
      continue;
    }
    if (Math.abs(amount) < HALF_CENT) continue;
    const lease = context.leases.find(
      candidate => candidate.contractId === pc.contractId,
    );
    // A departed lease whose departure settled part of the cost: only the
    // days after it count here, so its days do not explain the share.
    const isPartlySettled = (context.alreadySettled ?? []).some(
      line => line.contractId === pc.contractId && line.invoiceId === inv.id,
    );
    const tenantDays =
      lease && reader.isSplitByDays && !perOccupant && !isPartlySettled
        ? settledDaysOf(lease, pc, inv)
        : undefined;
    rows.push({
      id: `tenant-${pc.contractId}`,
      kind: "tenant",
      unitId: pc.unitId,
      unitLabel: unitLabelOf(pc.unitId),
      name: pc.tenantName,
      title: pc.tenantName,
      notes: addressNotesOf([pc.unitId]),
      share: unitShareOf(pc.unitId, pc.contractId),
      occupied:
        tenantDays != null
          ? { days: tenantDays, total: totalDays, period: costPeriodOf(inv) }
          : undefined,
      amount,
      vat: roundCents(pc.vatByInvoice?.[inv.id] ?? 0),
      contractId: pc.contractId,
    });
  }
  // An older snapshot does not hold each tenant's part of each cost.
  if (isUnknown) {
    return {
      rows: [],
      missing: getLocalizedText("costs.detail.split.not_per_cost"),
    };
  }

  // A departure holds the leaving tenant's share only (its owners are
  // empty): the rest of the cost is no part of it, and no owner's.
  if (context.isDeparture) return { rows: sortCostSplitRows(rows) };

  if (!context.owners) {
    // Older snapshots kept no owner parts: what the tenants do not carry.
    const rest = roundCents(inclOf(inv) - sum(rows.map(row => row.amount)));
    if (Math.abs(rest) >= HALF_CENT) {
      rows.push({
        id: "owner",
        kind: "owner",
        name: ownerPartName("owner"),
        title: getLocalizedText("costs.split.owner"),
        notes: [],
        amount: rest,
        vat: roundCents(btwOf(inv) - sum(rows.map(row => row.vat))),
      });
    }
    return { rows: sortCostSplitRows(rows) };
  }

  // The key that split the owners' part of a reading's or a metering
  // company's category, over the units of the cost's period.
  const ownerPartKey = reader.dist?.ownerPartKey;
  const ownerPartSplit =
    !reader.isSplitByDays && ownerPartKey && context.distribution
      ? keySplitsForCosts({
          keyDef: ownerPartKey,
          units: context.distribution.units,
          periods: [{ from: inv.periodFrom, to: inv.periodTo }],
          allUnits: context.distribution.allUnits,
          unitCount: context.distribution.unitCount,
        })[0]
      : undefined;
  // An owner's row's share: the unit's weight of that key for its part of
  // the owners' part ("10/85", Lewis 2026-10-19), else the unit's share of
  // the cost.
  const ownerRowShareOf = (
    unitId: string,
    part: "vacancy" | "owner",
  ): string | undefined =>
    part === "owner" && ownerPartSplit && ownerPartSplit.total > 0
      ? `${formatWeight(ownerPartSplit.weightOf(unitId))}/${formatWeight(
          ownerPartSplit.total,
        )}`
      : unitShareOf(unitId);

  // How an owner's part spreads over their units: the engine keeps one
  // line per owner and cost, the split shows one row per unit (Lewis,
  // 2026-10-19: never "bus 1, bus 2, bus 7" on one row). Its vacancy goes
  // by what each unit brings in (its weight of the key times its empty
  // days, or the empty unit's own reading), its fixed share by the weight
  // of the key that split it; to the cent of the engine's figures.
  // An owner's line of this cost, for its vacancy per unit (see
  // readingVacancyParts); set per owner below.
  let currentLine: OwnerCostLine | undefined;
  const vacancyPartsOf = (unitIds: string[]) =>
    currentLine
      ? readingVacancyParts({
          inv,
          context,
          reader,
          unitIds,
          line: currentLine,
        })
      : undefined;
  const ownerWeightsOf = (
    unitIds: string[],
  ): { vacancy?: number[]; base?: number[] } => {
    if (reader.isSplitByDays) {
      const split = reader.keySplitOf(inv);
      const weightOf = (unitId: string): number =>
        split && split.total > 0 ? split.weightOf(unitId) : 1;
      return {
        vacancy: unitIds.map(unitId =>
          reader.ownerCarriesNothingOf(unitId, inv)
            ? 0
            : weightOf(unitId) *
              Math.max(0, totalDays - reader.coveredDaysOf(unitId, inv)),
        ),
        base: unitIds.map(weightOf),
      };
    }
    const readingParts = vacancyPartsOf(unitIds);
    return {
      vacancy: readingParts?.map(part =>
        part.excl != null ? toCents(part.excl) : part.reading,
      ),
      base:
        ownerPartSplit && ownerPartSplit.total > 0
          ? unitIds.map(unitId => ownerPartSplit.weightOf(unitId))
          : undefined,
    };
  };
  // An amount over the weights in whole cents, its sign kept; undefined
  // when the weights do not carry all of it.
  const spread = (amount: number, weights?: number[]): number[] | undefined => {
    if (!weights) return undefined;
    const cents = toCents(amount);
    const parts = splitCents(Math.abs(cents), weights);
    if (parts.reduce((total, part) => total + part, 0) !== Math.abs(cents)) {
      return undefined;
    }
    return parts.map(part => fromCents(Math.sign(cents) * part));
  };

  for (const owner of context.owners) {
    const line = owner.lines.find(candidate => candidate.invoiceId === inv.id);
    if (!line) continue;
    currentLine = line;
    const ownerName = context.ownerNames?.[owner.ownerKey] || undefined;
    const ownerNotes = ownerName
      ? [
          getLocalizedText("costs.detail.split.owner_named", {
            owner: ownerName,
          }),
        ]
      : [];
    const ownerUnitIds = owner.unitIds.map(String);
    // The units the owner's part comes from: the unit of its own cost, else
    // those that did not have every day of the cost let.
    const contributing = line.unitId
      ? [String(line.unitId)]
      : ownerUnitIds.filter(
          unitId => !reader.ownerCarriesNothingOf(unitId, inv),
        );
    const unitIds = contributing.length > 0 ? contributing : ownerUnitIds;

    // The departure differences carry their own VAT; the rest of the line's
    // VAT goes over the vacancy and the fixed share as the owner's card
    // splits it.
    const diffs = line.departureDiffs ?? [];
    const { base: baseVat, vacancy: vacancyVat } = ownerLineVatByCause(line);
    const ownerPct =
      line.ownerPct ?? (reader.isPrivate ? 100 - tenantPct : undefined);
    const baseTitle =
      ownerPct != null
        ? getLocalizedText("costs.detail.owner_cost_share_pct", {
            pct: String(ownerPct),
          })
        : getLocalizedText("costs.split.owner");

    const readingParts = vacancyPartsOf(unitIds);
    // The vacancy row of a unit under a reading per occupant: its reading
    // and its share of the excess (M19).
    const readingShareOf = (
      unitId: string,
      excl: number,
    ): string | undefined => {
      const part = readingParts?.find(candidate => candidate.unitId === unitId);
      if (!part) return undefined;
      return readingVacancyText({
        reading: part.reading,
        total: context.split?.buildingTotal?.[reader.category],
        unit: CONSUMPTION_UNIT[reader.category],
        excess:
          part.readingPart == null
            ? 0
            : fromCents(toCents(excl) - toCents(part.readingPart)),
      });
    };
    // One part of the owner's line: a row per unit, or, when the units'
    // weights cannot carry it, one row naming each unit's address.
    const pushPart = (part: {
      kind: "vacancy" | "owner";
      excl: number;
      vat: number;
      weights?: number[];
    }) => {
      if (Math.abs(part.excl) < HALF_CENT) return;
      const isVacancy = part.kind === "vacancy";
      const title = isVacancy
        ? getLocalizedText("settlement.share_card.vacancy")
        : baseTitle;
      const name = ownerPartName(isVacancy ? "vacancy" : "owner", ownerName);
      const excls =
        unitIds.length === 1 ? [part.excl] : spread(part.excl, part.weights);
      const vats =
        unitIds.length === 1 ? [part.vat] : spread(part.vat, part.weights);
      if (!excls || !vats) {
        rows.push({
          id: `${part.kind}-${owner.ownerKey}`,
          kind: part.kind,
          name,
          title,
          notes: [...ownerNotes, ...addressNotesOf(unitIds)],
          amount: roundCents(part.excl + part.vat),
          vat: part.vat,
          ownerUnitIds,
        });
        return;
      }
      unitIds.forEach((unitId, index) => {
        const excl = excls[index];
        const vat = vats[index];
        if (Math.abs(excl) < HALF_CENT && Math.abs(vat) < HALF_CENT) return;
        const vacantDays =
          isVacancy && reader.isSplitByDays
            ? totalDays - reader.coveredDaysOf(unitId, inv)
            : undefined;
        rows.push({
          id: `${part.kind}-${owner.ownerKey}-${unitId}`,
          kind: part.kind,
          unitId,
          unitLabel: unitLabelOf(unitId),
          name,
          title,
          notes: [...ownerNotes, ...addressNotesOf([unitId])],
          // A unit's own cost: the owner's part is in the title. A
          // reading's vacancy: the unit's reading and its share of the
          // excess (M19).
          share:
            (isVacancy ? readingShareOf(unitId, excl) : undefined) ??
            (isVacancy || !reader.isPrivate
              ? ownerRowShareOf(unitId, part.kind)
              : undefined),
          occupied:
            vacantDays != null && vacantDays > 0
              ? {
                  days: vacantDays,
                  total: totalDays,
                  period: costPeriodOf(inv),
                  isVacant: true,
                }
              : undefined,
          amount: roundCents(excl + vat),
          vat,
          ownerUnitIds,
        });
      });
    };
    const weights = ownerWeightsOf(unitIds);
    pushPart({
      kind: "vacancy",
      excl: line.vacancy,
      vat: vacancyVat,
      weights: weights.vacancy,
    });
    pushPart({
      kind: "owner",
      excl: line.base,
      vat: baseVat,
      weights: weights.base,
    });
    diffs.forEach(diff => {
      const name = getLocalizedText(
        "settlement.summary.owner_departure_difference",
        {
          tenant: diff.tenantName,
        },
      );
      rows.push({
        id: `departure-${owner.ownerKey}-${diff.settlementId}-${diff.contractId}`,
        kind: "owner",
        unitId: diff.unitId,
        unitLabel: unitLabelOf(diff.unitId),
        name,
        title: name,
        notes: [...ownerNotes, ...addressNotesOf([diff.unitId])],
        amount: roundCents(diff.amount + diff.vat),
        vat: diff.vat,
        ownerUnitIds,
      });
    });
  }
  return { rows: sortCostSplitRows(rows) };
};

// ── The summaries' contexts ─────────────────────────────────────────────────

// A unit by its full address, "Scenariolaan 1 bus 6, 9000 Gent", its box
// spelled out (unitAddressLabel); else the address its building's units
// list gives, else its own name.
const unitLabelIn =
  (params: {
    assignable: {
      id: string;
      code: string;
      box?: string;
      address?: string;
      addressParts?: IAddressParts;
    }[];
    units?: { id: string; code: string; address?: string }[];
  }) =>
  (unitId: string): string => {
    const property = params.assignable.find(
      candidate => String(candidate.id) === String(unitId),
    );
    if (property?.addressParts) return unitAddressLabel(property);
    const unit = params.units?.find(
      candidate => String(candidate.id) === String(unitId),
    );
    return (
      unit?.address ||
      property?.address ||
      (property ? unitBoxLabel(property) : undefined) ||
      unit?.code ||
      unitId
    );
  };

// The wizard's summary: the live result and the draft's choices.
export const draftCostShareContext = (params: {
  result: Pick<
    ComputeResult,
    "perContract" | "owners" | "alreadySettled" | "unitSharesByInvoice"
  >;
  split: ICostShareSplitFigures;
  contracts: Pick<ContractRef, "id" | "unitId" | "startDate" | "endDate">[];
  catMeta: Record<string, CatMeta>;
  distribution?: ICardDistribution;
  ownerNames?: Record<string, string>;
  isDeparture?: boolean;
  assignable: {
    id: string;
    code: string;
    box?: string;
    address?: string;
    addressParts?: IAddressParts;
  }[];
  units?: { id: string; code: string; address?: string }[];
}): ICostShareContext => ({
  perContract: params.result.perContract,
  owners: params.result.owners,
  ownerNames: params.ownerNames,
  catMeta: params.catMeta,
  distribution: params.distribution,
  leases: params.contracts.map(contract => ({
    contractId: contract.id,
    unitId: contract.unitId,
    from: contract.startDate,
    to: contract.endDate,
  })),
  split: params.split,
  isDeparture: params.isDeparture,
  alreadySettled: params.result.alreadySettled,
  unitSharesByInvoice: params.result.unitSharesByInvoice,
  unitLabelOf: unitLabelIn(params),
});

// A confirmed settlement's Detail tab: its frozen snapshot, the costs are
// in it.
export const confirmedCostShareContext = (params: {
  settlement: Settlement;
  catMeta: Record<string, CatMeta>;
  distribution?: ICardDistribution;
  // The rental period each tenant was settled with (frozenContractDates).
  leaseDatesOf: (
    pc: PerContractResult,
  ) => { from: string; to: string } | undefined;
  assignable: {
    id: string;
    code: string;
    box?: string;
    address?: string;
    addressParts?: IAddressParts;
  }[];
}): ICostShareContext => {
  const { settlement } = params;
  const { snapshot } = settlement;
  const perContract = snapshot?.perContract ?? [];
  return {
    perContract,
    owners: snapshot?.owners,
    ownerNames: snapshot?.ownerNames,
    catMeta: params.catMeta,
    distribution: params.distribution,
    leases: perContract.flatMap(pc => {
      const dates = params.leaseDatesOf(pc);
      return dates
        ? [{ contractId: pc.contractId, unitId: pc.unitId, ...dates }]
        : [];
    }),
    split: snapshot,
    viewerPart: !!snapshot?.viewerPart,
    isDeparture: isDepartureSettlement(settlement),
    alreadySettled: snapshot?.alreadySettled,
    unitSharesByInvoice: snapshot?.unitSharesByInvoice,
    unitLabelOf: unitLabelIn({
      assignable: params.assignable,
      units: params.distribution?.units,
    }),
    lockedIn:
      settlement.status === "confirmed"
        ? settlementDisplayName(settlement)
        : undefined,
  };
};
